import React from "react";
import WarningPopup from "../../../components/popups/Warning";
import QuestionnaireFinalSectionValidation from "../QuestionnaireFinalSectionValidation";
import { DocumentWithRelations } from "../../../../../api/src/types";
import {
  COLOR_WARNING,
  POPUP_WARNING_BACKGROUND_COLOR_GRADIENT,
} from "../../../components/theme";
import InformationIcon from "../../../components/icons/InformationIcon";

export interface QuestionnairePopupManagerProps {
  validationMessagePopupOpen: boolean;
  setValidationMessagePopupOpen: (open: boolean) => void;
  userDocument: DocumentWithRelations | null | undefined;
  isLastSection: boolean;
  isDocumentGenerated?: boolean;
  hasAnswersModifiedAfterDocumentGeneration?: boolean;
  isDocumentInEditor?: boolean;
  isDocumentInProof?: boolean;
  isNotarizationComplete?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const QuestionnairePopupManager: React.FC<QuestionnairePopupManagerProps> = ({
  validationMessagePopupOpen,
  setValidationMessagePopupOpen,
  userDocument,
  isLastSection,
  isDocumentGenerated,
  hasAnswersModifiedAfterDocumentGeneration,
  isDocumentInEditor,
  isDocumentInProof,
  onConfirm,
  onCancel,
}) => {
  if (!validationMessagePopupOpen) return null;
  // Show warning popup when:
  // 1. Document exists (isDocumentGenerated)
  // 2. User does not have modified answers after document was generated (!hasAnswersModifiedAfterDocumentGeneration)
  // 3. Document hasn't been edited yet (isDocumentInEditor)
  // 4. Document is not in proof stage (isDocumentInProof)
  // This popup warns users that they need to regenerate their document after making questionnaire changes
  if (
    isDocumentGenerated &&
    !isDocumentInEditor &&
    !isDocumentInProof &&
    !hasAnswersModifiedAfterDocumentGeneration
  ) {
    return (
      <WarningPopup
        title="Are you sure you want to make this change?"
        icon={<InformationIcon color={COLOR_WARNING} />}
        backgroundColor={POPUP_WARNING_BACKGROUND_COLOR_GRADIENT}
        description="If you make a change to your questionnaire you’ll need to regenerate your prenuptial agreement in the documents section as well."
        buttons={{
          confirmText: "Okay, let’s proceed",
          cancelText: "Nevermind",
          onConfirm,
          onCancel,
        }}
      />
    );
  }

  // No state selected popup
  if (!userDocument?.stateId) {
    return (
      <WarningPopup
        title="Almost there!"
        description="We just need a bit more info before moving forward. Please tell us your state so we can continue with your process."
        buttons={{
          confirmText: "Go back and answer",
          onConfirm,
        }}
      />
    );
  }

  // Last section popup
  if (isLastSection) {
    return (
      <WarningPopup
        title="You are almost there!"
        description={
          <QuestionnaireFinalSectionValidation
            setValidationMessagePopupOpen={setValidationMessagePopupOpen}
          />
        }
        buttons={{
          confirmText: "Go back and answer",
          onConfirm,
        }}
      />
    );
  }

  // Regular section popup
  return (
    <WarningPopup
      title="You have unanswered questions"
      description="Please make sure to answer all required questions before continuing."
      buttons={{
        confirmText: "Go back and answer",
        cancelText: "I'll answer them later",
        onConfirm,
        onCancel,
      }}
    />
  );
};

export default QuestionnairePopupManager;
